import { ReactNode, useMemo, useState } from 'react';

import { withRoleAccess } from '@app/routes';
import { useUserRole } from '@entities/user';
import { useUpdateWalletMutation, walletParserModes, walletStatuses } from '@entities/wallets';
import type {
  IWalletClickhouse,
  IWalletClickhouseTableColumns,
  IWalletClickhouseTableColumnsWithdrawal,
  TWalletType,
} from '@entities/wallets';
import CopyToClipboardButton from '@features/copy-to-clipboard-button';
import { ModalEditEntity } from '@features/modal-entity-edit';
import { ModalViewEntity } from '@features/modal-entity-view';
import ModalOpenBtn from '@features/modal-open-btn';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import { RefetchAuto } from '@features/refetch-auto';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import { TableControlMenu } from '@features/table-control-menu';
import { FilterInput, FilterSelect, FilterControlBlock } from '@features/table-filter';
import { WalletCredentials } from '@features/wallets';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MenuItem, IconButton, Tooltip, Checkbox } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { TBody } from '@shared/components/Table';
import { getTypedObjectKeys } from '@shared/types/typedObjectEntries';

import { EditWalletSettings } from '../components/EditWalletSettings';
import { NewWallet } from '../components/NewWallet';
import { NewWalletMID } from '../components/NewWalletMID';
import { ParserModeToggler } from '../components/ParserModeToggler';
import { StatusToggler } from '../components/StatusToggler';
import { StopTrafficBtn } from '../components/StopTrafficBtn';

type WalletKey =
  | keyof IWalletClickhouseTableColumns
  | keyof IWalletClickhouseTableColumnsWithdrawal
  | 'wallet_policy';

interface IProps {
  type: TWalletType;
  refetch?: () => void;
  setRefreshInterval?: (interval: string) => void;
  refreshInterval?: string;
  wallets?: IWalletClickhouse[];
}

export const useTableSettingsClickhouse = ({
  type,
  refetch,
  refreshInterval,
  setRefreshInterval,
  wallets,
}: IProps) => {
  const [isExpandAll, setIsExpandAll] = useState(true);
  const [selectedWalletIds, setSelectedWalletIds] = useState<Set<string>>(new Set());
  const userRole = useUserRole();

  const isSelected = (id: string) => selectedWalletIds.has(id);

  const toggleSelectParent = (wallet: IWalletClickhouse) => {
    const nextSelected = new Set(selectedWalletIds);
    if (nextSelected.has(wallet.id)) {
      nextSelected.delete(wallet.id);
    } else {
      nextSelected.add(wallet.id);
    }
    setSelectedWalletIds(nextSelected);
  };

  const toggleSelectSingle = (id: string) => {
    const nextSelected = new Set(selectedWalletIds);
    if (nextSelected.has(id)) {
      nextSelected.delete(id);
    } else {
      nextSelected.add(id);
    }
    setSelectedWalletIds(nextSelected);
  };

  const clearSelection = () => setSelectedWalletIds(new Set());

  const parentWallets = useMemo(
    () => (Array.isArray(wallets) ? wallets.filter(w => w.parent_id === null) : []),
    [wallets],
  );

  const areAllParentsSelected =
    parentWallets.length > 0 && parentWallets.every(w => selectedWalletIds.has(w.id));
  const someParentsSelected =
    parentWallets.length > 0 && parentWallets.some(w => selectedWalletIds.has(w.id));

  const toggleSelectAllParents = () => {
    const next = new Set(selectedWalletIds);
    if (areAllParentsSelected) {
      parentWallets.forEach(w => next.delete(w.id));
    } else {
      parentWallets.forEach(w => next.add(w.id));
    }
    setSelectedWalletIds(next);
  };

  // THead settings
  const columnTitlesDeposit: IWalletClickhouseTableColumns = {
    wallet_name: 'Name',
    wallet_address: 'Wallet address',
    status_toggle: 'Status toggle',
    status: 'Status',
    stop_traffic: 'Stop traffic',

    mid_name: 'MID',
    actions: 'Actions',
  };

  const columnTitlesWithdrawal: IWalletClickhouseTableColumnsWithdrawal = {
    wallet_name: 'Name',
    wallet_address: 'Wallet address',
    status_toggle: 'Status toggle',
    status: 'Status',
    parser_toggle: 'Parser toggle',
    mode: 'Parser mode',
    mid_name: 'MID',
    actions: 'Actions',
  };

  if (userRole === 'operator') {
    delete columnTitlesDeposit.stop_traffic;
  }

  const columnTitles = type === 'deposit' ? columnTitlesDeposit : columnTitlesWithdrawal;

  const renderFilterElement = (key: WalletKey) => {
    switch (key) {
      case 'wallet_name': {
        return (
          <div className='wallet-name__filter'>
            <Tooltip title='Expand/Collapse ALL' placement='right'>
              <IconButton onClick={() => setIsExpandAll(!isExpandAll)} aria-label='copy'>
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title='Select/Deselect all parents' placement='right'>
              <Checkbox
                size='small'
                sx={{
                  color: 'text.primary',
                  '&.Mui-checked': { color: 'text.primary' },
                  '&.MuiCheckbox-indeterminate': { color: 'text.primary' },
                }}
                checked={areAllParentsSelected}
                indeterminate={!areAllParentsSelected && someParentsSelected}
                onClick={e => e.stopPropagation()}
                onChange={toggleSelectAllParents}
              />
            </Tooltip>

            <FilterInput name={key} />
          </div>
        );
      }
      case 'status_toggle':
      case 'parser_toggle':
      case 'wallet_address':
      case 'stop_traffic':
        return null;

      case 'status':
        return <FilterSelect name={key} options={walletStatuses} />;

      case 'mode':
        return <FilterSelect name={key} options={walletParserModes} />;

      case 'wallet_policy':
        return <FilterSelect name={key} options={[]} />;

      case 'actions':
        const NewWalletBtn = withRoleAccess(ModalOpenBtn, ['admin', 'operator']);

        return (
          <FilterControlBlock showVisibilityBtn={false}>
            <TableControlMenu>
              <MenuItem>
                <NewWalletBtn
                  text='New wallet'
                  icon={<AccountBalanceWalletIcon color='success' />}
                  modalContent={<NewWallet type={type} refetch={refetch} />}
                />
              </MenuItem>
              <MenuItem data-menu-type='dropdown'>
                <RefetchAuto
                  refreshInterval={refreshInterval}
                  onChangeInterval={setRefreshInterval}
                />
              </MenuItem>
            </TableControlMenu>
          </FilterControlBlock>
        );

      default:
        return <FilterInput name={key} />;
    }
  };

  // TBody settings

  const renderRow = (wallet: IWalletClickhouse, defaultRow: ReactNode) => {
    const isParentWallet = wallet.parent_id === null;
    const hasChildWallets = wallet.childs.length > 0;

    const customRow = () => {
      return (
        <tr
          key={isParentWallet ? wallet.id : wallet.id + 'child'}
          className={`${isParentWallet ? 'parent-wallet' : 'child-wallet'}`}
        >
          <td colSpan={Object.keys(columnTitles).length}>
            <Accordion className='accordion-block' defaultExpanded={isExpandAll}>
              <table>
                <tbody>
                  <tr>
                    {getTypedObjectKeys(columnTitles).map(key => {
                      return (
                        <td key={key}>
                          <div className='table-column__name'>{columnTitles[key]}:</div>
                          {renderItem(key, wallet)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
              <AccordionDetails className='accordion-details'>
                <table>
                  <TBody items={wallet.childs} renderItem={renderItem} />
                </table>
              </AccordionDetails>
            </Accordion>
          </td>
        </tr>
      );
    };

    return hasChildWallets ? customRow() : defaultRow;
  };

  const renderItem = (key: WalletKey, wallet: IWalletClickhouse) => {
    const isParentWallet = wallet.parent_id === null;

    const StopTraffic = withRoleAccess(StopTrafficBtn, ['admin', 'operator', 'provider']);

    switch (key) {
      case 'wallet_name': {
        if (isParentWallet) {
          const liveChilds = wallet.childs.filter(child => child.status === 'live').length;

          return (
            <div className='wallet-name-block'>
              <div className='wallet-name'>
                <div className='table-cell-items' style={{ overflow: 'hidden' }}>
                  <Checkbox
                    size='small'
                    sx={{
                      color: 'text.primary',
                      '&.Mui-checked': { color: 'text.primary' },
                      '&.MuiCheckbox-indeterminate': { color: 'text.primary' },
                    }}
                    checked={isSelected(wallet.id)}
                    onClick={e => e.stopPropagation()}
                    onChange={() => toggleSelectParent(wallet)}
                  />
                  <AccordionSummary
                    className='accordion-summary'
                    expandIcon={<ExpandMoreIcon />}
                    key={key}
                  >
                    <span title={wallet.wallet_name}>{wallet.wallet_name}</span>
                  </AccordionSummary>
                  <CopyToClipboardButton text={wallet.wallet_name} />
                </div>
              </div>
              {(liveChilds >= 1 || wallet.status === 'live') && (
                <span className='wallet-status__indicator'>
                  {liveChilds >= 1 ? liveChilds : ''}
                </span>
              )}
            </div>
          );
        }

        return <span title={wallet.wallet_name}>{wallet.wallet_name}</span>;
      }

      case 'wallet_address':
        return <ReduceAndCopyString text={wallet[key]} needReduce={false} />;

      // case 'wallet_policy':dwldqmdqdmlwqmdmdqwm;ldqwmldmqwdmq;wdmq;wldmqw;mdwqmdqw;md;wqlmd;qlwdmqwdmqwmdql;w
      //   return (
      //     /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      //     <span title={(wallet as any).wallet_policy}>{(wallet as any).wallet_policy ?? '-'}</span>
      //   );

      case 'status_toggle':
        return <StatusToggler wallet={wallet} refetch={refetch} />;

      case 'stop_traffic':
        return isParentWallet ? <StopTraffic wallet={wallet} refetch={refetch} /> : null;

      case 'parser_toggle':
        return <ParserModeToggler id={wallet.id} parserMode={wallet.mode} refetch={refetch} />;

      case 'mid_name':
        return <span title={wallet.mid_name}>{wallet.mid_name}</span>;

      case 'status':
        return (
          <span title={wallet.status} className={`wallet-status-${wallet.status}`}>
            {wallet.status}
          </span>
        );

      case 'actions': {
        const excludeKeysEdit: (keyof IWalletClickhouse)[] = [
          'id',
          'parent_id',
          'type',
          'status',
          'mode',

          'mid_id',
          'mid_name',
          'provider_name',
          'bank_id',
          'bank_name',
          'bank_tr_validator',
          'bank_holder_name',
          'bank_account',
          'bank_balance',
          'bank_details',
          'sum_per_hour',
          'sum_per_day',
          'currency',
          'method_name',
          'merchant_short_name',
          'ar_per_hour',
          'ar_per_day',
          'system_connection_date',
          'merchant_is_test',
          'created_at',
          'updated_at',
          'wallet_reason',
          'mid_traffic',

          // Objects
          'childs',
        ];

        const excludeKeysView: (keyof IWalletClickhouse)[] = [
          'mid_id',
          'mid_name',
          'provider_id',
          'bank_id',
          'bank_tr_validator',
          'bank_holder_name',
          'bank_account',
          'bank_balance',
          'bank_details',
          'sum_per_hour',
          'sum_per_day',
          'currency',
          'method_name',
          'ar_per_hour',
          'ar_per_day',
          'system_connection_date',
          'merchant_is_test',
          'created_at',
          'updated_at',
          'wallet_reason',
          'mid_traffic',
        ];

        if (type === 'deposit') {
          excludeKeysView.push('mode');
        }

        const isParentWallet = wallet.parent_id === null;
        if (!isParentWallet) {
          excludeKeysEdit.push(
            'wallet_address',
            'provider_id',
            'bank_code',
            'wallet_holder_name',
            'bank_account_number',
            'wallet_policy',
            'limit_preset',
          );
        } else {
          excludeKeysView.push('parent_id', 'merchant_short_name');
        }

        const handleEditWallet = (formData: FormData) => {
          formData.set('name', formData.get('wallet_name') as string);
          formData.delete('wallet_name');
          return formData;
        };

        let additionalKeys: Partial<Record<keyof IWalletClickhouse, string>>[] = [
          { login_credentials_json: '', provider_id: wallet.provider_id },
        ];

        if (!isParentWallet || type !== 'withdraw') {
          additionalKeys = [{ provider_id: wallet.provider_id }];
        }

        const actionBtnsConfig: ITableActionsConfig = {
          add: {
            isEnabled: isParentWallet,
            isVisible: true,
            popupElem: (
              <NewWalletMID
                parent_id={wallet.id}
                parent_name={wallet.wallet_name}
                refetch={refetch}
                shouldSubmitDefaultSettings={false}
              />
            ),
            popupSize: 'md',
            allowedRoles: ['admin', 'operator'],
          },
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntity
                title='Edit Wallet'
                entity={wallet}
                excludeKeys={excludeKeysEdit}
                renamedLabels={{
                  provider_id: 'Provider',
                }}
                keysOrder={['id', 'bank_code', 'wallet_name', 'bank_account_number']}
                additionalKeys={additionalKeys}
                useEditEntityMutation={useUpdateWalletMutation}
                onTransformFormData={handleEditWallet}
                refetch={refetch}
              />
            ),
            popupSize: 'md',
            allowedRoles: ['admin', 'merchant', 'operator'],
          },
          'edit-settings': {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <EditWalletSettings
                parent_id={wallet.id}
                parent_name={wallet.wallet_name}
                refetch={refetch}
              />
            ),
            popupSize: 'md',
          },
          view: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalViewEntity
                entity={wallet}
                title='View Wallet'
                keysOrder={['id', 'bank_code', 'wallet_name', 'bank_account_number']}
                excludeKeys={excludeKeysView}
                renamedLabels={{ name: 'Wallet name', mode: 'Parser mode' }}
                additionalData={
                  type !== 'deposit'
                    ? [
                        {
                          label: 'Credentials:',
                          element: <WalletCredentials walletId={wallet.id} />,
                        },
                      ]
                    : undefined
                }
              />
            ),
            popupSize: 'md',
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return wallet[key];
    }
  };

  return {
    columnTitles,
    renderFilterElement,
    renderRow,
    renderItem,
    selectedWalletIds: Array.from(selectedWalletIds),
    isSelected,
    toggleSelectParent,
    toggleSelectSingle,
    clearSelection,
  };
};
